import { useRef, useState } from "react";
import { useElectronAudioLift } from "@/utils/electronAudio";
import { IoVolumeHigh, IoVolumeMute } from "react-icons/io5";
import { ChallengeInfoResponse } from "@devour/client";
import { useGetAsset } from "@/hooks/useAsset";

// VideoSection config type - matches backend ChallengeVideoSection model
interface VideoSectionConfig {
    videoUrl?: string;
    posterImageId?: string;
    autoplay?: boolean;
    loop?: boolean;
    showOnPage?: boolean;
}

// Extended challenge type for fields not yet in generated client
interface ExtendedChallenge extends ChallengeInfoResponse {
    videoSection?: VideoSectionConfig;
}

interface ChallengeVideoProps {
    challenge: ChallengeInfoResponse;
}

const CHALLENGE_VIDEO_SOUND_KEY = "challenge-video-sound-enabled";

/**
 * Video section component for challenge pages.
 * Reads configuration from challenge.videoSection:
 * - videoUrl: URL of the video to play
 * - posterImageId: Asset ID for the video poster/thumbnail
 * - autoplay: Whether to autoplay (default true)
 * - loop: Whether to loop (default true)
 * - showOnPage: Whether to render at all (default true)
 */
export const ChallengeVideo = ({ challenge }: ChallengeVideoProps) => {
    const extendedChallenge = challenge as ExtendedChallenge;
    const videoSection = extendedChallenge?.videoSection;

    // Don't render if explicitly hidden or no video URL configured
    if (videoSection?.showOnPage === false || !videoSection?.videoUrl) {
        return null;
    }

    const videoRef = useRef<HTMLVideoElement>(null);
    const hasSoundEnabled = localStorage.getItem(CHALLENGE_VIDEO_SOUND_KEY) === "true";
    const [isMuted, setIsMuted] = useState(!hasSoundEnabled);
    const [showPlayOverlay, setShowPlayOverlay] = useState(!hasSoundEnabled);

    // Electron: only lift the shell's renderer mute once the user unmutes this
    // video — an autoplay-muted reel must not grab the game's audio until then
    // (see @/utils/electronAudio). No-ops on plain web.
    useElectronAudioLift(!showPlayOverlay && !isMuted);

    // Fetch poster image asset if configured
    const { data: posterAsset } = useGetAsset(videoSection?.posterImageId || "");
    const posterUrl = posterAsset?.url || posterAsset?.pipeUrl;

    // Get config values with defaults
    const videoUrl = videoSection.videoUrl;
    const autoplay = videoSection.autoplay !== false; // Default true
    const loop = videoSection.loop !== false; // Default true

    const handlePlayWithSound = () => {
        setShowPlayOverlay(false);
        setIsMuted(false);
        localStorage.setItem(CHALLENGE_VIDEO_SOUND_KEY, "true");
        if (videoRef.current) {
            videoRef.current.muted = false;
            videoRef.current.play().catch(() => {
                // Silently handle play failures (unsupported codec, autoplay blocked, etc.)
            });
        }
    };

    const handleVideoEnded = () => {
        // Loop the video if configured
        if (loop && videoRef.current) {
            videoRef.current.play().catch(() => {
                // Silently handle play failures
            });
        }
    };

    const toggleMute = () => {
        const newMuted = !isMuted;
        setIsMuted(newMuted);
        localStorage.setItem(CHALLENGE_VIDEO_SOUND_KEY, newMuted ? "false" : "true");
    };

    return (
        <div className="challenge-page_video-container">
            <video
                ref={videoRef}
                className="challenge-page_video"
                src={videoUrl}
                autoPlay={autoplay}
                playsInline={true}
                preload="metadata"
                muted={showPlayOverlay || isMuted}
                onEnded={handleVideoEnded}
                poster={posterUrl}
            />
            {showPlayOverlay &&
                <button
                    className="challenge-page_video-play-overlay"
                    onClick={handlePlayWithSound}
                    aria-label="Play video with sound"
                >
                    <IoVolumeHigh />
                    <span>Tap for Sound</span>
                </button>
            }
            {!showPlayOverlay &&
                <button
                    className="challenge-page_video-mute-btn"
                    onClick={toggleMute}
                    aria-label={isMuted ? "Unmute video" : "Mute video"}
                >
                    {isMuted ? <IoVolumeMute /> : <IoVolumeHigh />}
                </button>
            }
        </div>
    );
};

export default ChallengeVideo;
